<template>
  <section class="hero">
    <div class="hero__stage" :class="{ 'hero__stage--plated': plated }" aria-hidden="true">
      <img
        ref="plate"
        class="hero__art"
        :src="heroLarge"
        :srcset="`${heroSmall} 900w, ${heroLarge} 1456w`"
        sizes="100vw"
        alt=""
        fetchpriority="high"
        decoding="async"
        @load="plated = true"
        @error="plated = true"
      />
      <!--
        Sits between the plate and the vignette on purpose: the weather is
        part of the picture, so the same darkening and the same fade to
        --surface-0 apply to it.
      -->
      <!-- The ring sits higher and slightly right of centre in this plate; the
           light has to follow it or the strike lights empty cloud. -->
      <HeroAtmosphere
        class="hero__weather"
        :plate="heroLarge"
        :depth="heroDepth"
        :focus="[0.49, 0.31]"
        :parallax="0"
      />
      <!--
        The band, in three depth layers over the weather.

        Each layer is a full-frame transparent image carrying the same
        `object-fit: cover` box as the plate, so they register exactly at any
        viewport without a line of positioning arithmetic. They sit above the
        canvas because the canvas redraws the plate; a figure drawn into that
        plate could only be moved by displacing the plate itself, which smears
        the silhouette against the background it has no way to uncover.
      -->
      <img class="hero__band hero__band--far" :src="figFar" alt="" aria-hidden="true" decoding="async" />
      <img class="hero__band hero__band--mid" :src="figMid" alt="" aria-hidden="true" decoding="async" />
      <img class="hero__band hero__band--near" :src="figNear" alt="" aria-hidden="true" decoding="async" />
      <div class="hero__vignette"></div>
      <div class="hero__fade"></div>
    </div>

    <div class="hero__content">
      <p class="hero__eyebrow">{{ t('home.alphaNote') }}</p>
      <TitleComponent>Dimension&#8209;X</TitleComponent>
      <p class="hero__tagline">{{ t('home.tagline') }}</p>
      <p class="hero__lead">{{ t('home.heroLead') }}</p>

      <!--
        One action. The hero used to carry three buttons of three different
        weights inside a bordered panel — register, "what is this", and a
        second login that duplicated the one in the header two inches above.
        Nothing in that row read as the thing to press.
      -->
      <div class="hero__actions">
        <LandingButton variant="accent" :to="ctaTarget">{{ ctaLabel }}</LandingButton>
        <router-link class="hero__secondary" :to="{ name: 'WhatIsIt' }">{{ t('home.ctaSecondary') }}</router-link>
      </div>
    </div>

    <a class="hero__cue" href="#pitch">
      <span class="hero__cue-label">{{ t('home.scrollCue') }}</span>
      <span class="hero__cue-rule" aria-hidden="true"></span>
    </a>
  </section>
</template>

<script setup>
import TitleComponent from '@/components/TitleComponent.vue';
import HeroAtmosphere from '@/components/HeroAtmosphere.vue';
import LandingButton from '@/components/btn/LandingButton.vue';
import { usePrimaryCta } from '@/composables/usePrimaryCta.js';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
/*
 * The plate ships EMPTY — no figures on it.
 *
 * The figures are eight separate renders (one each, about twice the resolution
 * of the old all-in-one sheet), grouped into three depth bands and stacked over
 * the plate at runtime. They are not baked in, because a baked figure can only
 * be moved by displacing the whole plate, and displacing a flat painting has no
 * background behind a figure to uncover — so the silhouette smears.
 *
 * Rebuilt by Art/scripts/build-hero-march.py; the layout table in that script
 * IS the composition. Do not edit these webp files by hand.
 */
import heroLarge from '@/assets/images/backgrounds/hero/rift-march.webp';
import heroSmall from '@/assets/images/backgrounds/hero/rift-march-sm.webp';
/* Depth of the plate alone, from a monocular pass. It no longer carries figure
 * silhouettes — there are no figures in the plate to carry — and with parallax
 * off its remaining job is telling the strike lighting which pixels are ground
 * and which are cloud. Near is white; the sky is zeroed. */
import heroDepth from '@/assets/images/backgrounds/hero/rift-march-depth.webp';
/* The figures, grouped by distance so each group can drift by its own amount. */
import figFar from '@/assets/images/backgrounds/hero/rift-march-fig-far.webp';
import figMid from '@/assets/images/backgrounds/hero/rift-march-fig-mid.webp';
import figNear from '@/assets/images/backgrounds/hero/rift-march-fig-near.webp';

const { t } = useI18n();
const { ctaTarget, ctaLabel } = usePrimaryCta();

/*
 * The band waits for the ground.
 *
 * The four layers are four separate requests, and the figures are the small
 * ones: 16–66 KB against the plate's 129 KB. So they finish first, and for as
 * long as that gap lasts the hero is a row of black cut-outs standing on
 * nothing — the flash people were seeing on reload. Holding them until the
 * plate is up costs nothing (they are already downloaded; this only delays the
 * paint) and the stage carries the plate's own darkness underneath, so the
 * wait reads as the picture fading up rather than as a missing layer.
 */
const plate = ref(null);
const plated = ref(false);

/* A cached plate can be complete before Vue attaches the listener, and then
 * `load` never fires and the band never appears. */
onMounted(() => {
  if (plate.value?.complete) plated.value = true;
});
</script>

<style scoped>
/*
 * The hero is one composed frame, not a random one.
 *
 * It previously picked a background at random from six very different
 * pictures — two close-up portraits, two split-screen compositions and two
 * ultra-wide establishing shots. A layout cannot be composed against an image
 * whose focal point, palette and horizon move on every reload, which is why
 * the type sat in a centred blob that happened to land on a face as often as
 * on empty sky.
 *
 * The frame is now fixed: the ultra-wide rift shot, whose teal is the same
 * hue as --accent and whose lower third is dark and empty. The type sits in
 * that lower third, so the rift reads as the subject and the words sit in the
 * negative space under it — the poster arrangement, rather than text stamped
 * across the middle of a picture.
 */
.hero {
  position: relative;
  /* Pulled up under the sticky header so the artwork runs to the top of the
   * window; the padding puts the content back below it. */
  margin-top: calc(var(--header-h) * -1);
  padding: var(--header-h) var(--page-gutter) var(--space-8);
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  /* Content in the lower third. `justify-content: center` is what put it over
   * the middle of the picture. */
  justify-content: flex-end;
  align-items: center;
  text-align: center;
  overflow: hidden;
  isolation: isolate;
}

.hero__stage {
  position: absolute;
  inset: 0;
  z-index: -1;
  /* The plate's own darkest value. Every layer above this is an image that
   * arrives when it arrives; this is what they arrive onto, so the hero is
   * never a transparent hole over whatever the page happens to be. */
  background: #060a0f;
}

.hero__art {
  width: 100%;
  height: 100%;
  will-change: translate;
  object-fit: cover;
  /* The rift sits above the middle of the frame; anchoring the crop there
   * keeps it on screen when a tall viewport crops the sides. */
  object-position: 50% 28%;
  display: block;
  animation: hero-drift 32s var(--ease) forwards, hero-scene 41s ease-in-out infinite;
}

/* The weather has to ride the push-in with the plate. Without the same
 * transform, the lit cloud and the rift's halo drift a few percent off the
 * artwork they are supposed to be lighting during the opening seconds.
 *
 * It carries the scene drift for the same reason: the canvas redraws the
 * plate, so the plate and the canvas must move as one object or the lighting
 * separates from the thing it is lighting. */
.hero__weather {
  animation: hero-drift 32s var(--ease) forwards, hero-scene 41s ease-in-out infinite;
}

/*
 * The scene drifts; the band does not.
 *
 * The figures were the moving part at first, and it was wrong: a person
 * shifting while the ground under them holds still reads as the person
 * sliding, not as depth. Locking the band and moving the world behind it is
 * the way round that reads as a camera breathing over a static subject.
 *
 * It is a translation of the whole plate rather than a depth displacement.
 * Displacement by depth moves the near ground MOST and the sky not at all —
 * which would drag the ground out from under the figures' feet, the one place
 * a viewer is guaranteed to be looking. A uniform drift slides everything
 * equally, so the feet stay planted to within a pixel or two.
 *
 * 41s, against the bands' 32s push-in, so the two never resolve together.
 */
@keyframes hero-scene {
  0%   { translate: 0 0; }
  27%  { translate: -0.18% 0.06%; }
  53%  { translate: -0.07% -0.11%; }
  78%  { translate: 0.14% 0.05%; }
  100% { translate: 0 0; }
}

/*
 * The band.
 *
 * Three layers, each carrying the plate's exact crop — same size, same
 * object-fit, same object-position — so they register with the artwork at
 * every viewport without a single computed offset. Get any one of those three
 * wrong and the figures walk off the ground they are standing on.
 *
 * The band is deliberately STILL. It carries the opening push-in and nothing
 * else, so the figures stay locked to the ground they stand on while the scene
 * moves behind them. Do not give these layers their own motion: the ground is
 * painted into the plate, so anything that moves a figure independently of the
 * plate makes the figure look like it is sliding rather than standing.
 *
 * They are separate layers anyway, and must stay separate — that is what
 * stopped the figures tearing. The pointer parallax used to displace one flat
 * painting by a depth map, and a near silhouette has no background behind it
 * to uncover, so its edge pixels stretched across the gap.
 */
.hero__band {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 28%;
  display: block;
  pointer-events: none;
  animation: hero-drift 32s var(--ease) forwards;
  /* Hidden until the plate they stand on is up — see `plated` in the script.
   * Not `visibility`, because a cross-fade over an already-painted plate is
   * invisible where a pop is not. */
  opacity: 0;
  transition: opacity 260ms var(--ease);
}

.hero__stage--plated .hero__band {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .hero__art,
  .hero__weather {
    animation: hero-drift 32s var(--ease) forwards;
  }
}

/* Darkens the edges so the rift reads as the brightest thing in the frame and
 * the eye lands there first. */
.hero__vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 85% 70% at 50% 34%,
    transparent 0%,
    rgba(4, 7, 11, 0.22) 62%,
    rgba(4, 7, 11, 0.58) 100%
  );
}

/*
 * The seam. The hero used to stop dead against the page background — a hard
 * horizontal edge across the viewport that broke the illusion the moment you
 * started scrolling. This dissolves the picture into --surface-0 over the
 * bottom half, which is also what makes the type down there legible without
 * putting a panel behind it.
 *
 * The ramp used to be 72% opaque two-thirds down, which erased the figures,
 * the ruined skyline and the wet ground — the whole foreground of the plate —
 * to buy more contrast than the type needed. It is held back now: the picture
 * stays readable through the middle, and the seam does its dissolving in the
 * last quarter, where there is nothing left to lose.
 */
.hero__fade {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(10, 14, 20, 0.08) 44%,
    rgba(10, 14, 20, 0.44) 68%,
    rgba(10, 14, 20, 0.82) 87%,
    var(--surface-0) 100%
  );
}

/*
 * The type's own shadow, not the picture's.
 *
 * Lifting the fade off the lower frame handed the foreground back to the
 * artwork and took the lead paragraph from 5.7:1 down to 3.6:1 — under AA.
 * Darkening the whole plate again to fix a problem that exists in one column
 * of text is the trade that lost the figures in the first place. This is the
 * same darkness, scoped: an ellipse behind the words, transparent by the time
 * it reaches the figures at the sides and the ground below.
 */
.hero__content::before {
  content: '';
  position: absolute;
  /* Generous box, and radii of exactly half of it: the gradient reaches full
   * transparency precisely at the mid-edges, so the rectangle never shows.
   * Anything larger gets clipped by its own box and draws a straight line
   * across the picture. */
  inset: -34% -26% -26%;
  z-index: -1;
  pointer-events: none;
  background: radial-gradient(
    ellipse 50% 50% at 50% 50%,
    rgba(6, 10, 16, 0.74) 0%,
    rgba(6, 10, 16, 0.56) 40%,
    rgba(6, 10, 16, 0.24) 70%,
    transparent 100%
  );
}

.hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  width: 100%;
  max-width: 46rem;
  margin-bottom: var(--space-9);
}

.hero__eyebrow {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
  color: var(--accent-dim);
  margin: 0;
}

.hero__tagline {
  font-size: var(--text-xl);
  font-weight: var(--weight-medium);
  line-height: var(--leading-snug);
  color: var(--text);
  max-width: 30ch;
  margin: 0;
  text-wrap: balance;
}

.hero__lead {
  font-size: var(--text-base);
  line-height: var(--leading-body);
  color: var(--text-muted);
  max-width: 46ch;
  margin: 0;
  text-wrap: pretty;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-5);
  margin-top: var(--space-3);
}

/* The demoted second action. A text link, not a second button — so the one
 * filled button is unambiguously the thing to press. */
.hero__secondary {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-muted);
  text-decoration: none;
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--border-strong);
  transition: color var(--duration) var(--ease), border-color var(--duration) var(--ease);
}

.hero__secondary:hover {
  color: var(--text);
  border-bottom-color: var(--accent-line);
  text-decoration: none;
}

/* ---- Scroll cue ------------------------------------------------------
 * There was no indication the page continued below the hero, which is part of
 * why the landing read as "a wordmark and some buttons".
 */
.hero__cue {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-faint);
  text-decoration: none;
  transition: color var(--duration) var(--ease);
}

.hero__cue:hover {
  color: var(--text-muted);
  text-decoration: none;
}

.hero__cue-label {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
}

.hero__cue-rule {
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, var(--border-strong), transparent);
  overflow: hidden;
  position: relative;
}

.hero__cue-rule::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  height: 12px;
  background: var(--accent-line);
  animation: cue-fall 2.4s var(--ease) infinite;
}

/* A slow push-in. Cinematic language, and cheap: one transform on one
 * element, and it settles rather than looping. */
@keyframes hero-drift {
  from { transform: scale(1.06); }
  to { transform: scale(1); }
}

@keyframes cue-fall {
  0% { transform: translateY(-14px); opacity: 0; }
  40% { opacity: 1; }
  100% { transform: translateY(40px); opacity: 0; }
}

@media (max-width: 62rem) {
  .hero {
    margin-top: calc(var(--header-h-sm) * -1);
    padding-top: var(--header-h-sm);
  }
}

@media (max-width: 48rem) {
  .hero {
    /* A short landscape phone should not force a full screen of picture
     * before any words appear. */
    min-height: max(100dvh, 34rem);
    padding-bottom: var(--space-6);
  }

  .hero__content {
    margin-bottom: var(--space-7);
    gap: var(--space-3);
  }

  .hero__art {
    /* Portrait crops the sides hard; pulling the crop up keeps the rift and
     * the figures in frame instead of filling the screen with ground. */
    object-position: 50% 22%;
  }

  .hero__actions {
    gap: var(--space-4);
  }

  .hero__cue {
    display: none;
  }
}
</style>
